import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';

import { configurationService } from '../../services/configurationService';
import { ApiError } from '../../services/apiClient';

const RETENTION_KEY = 'Retention.MinRetentionDays';
const RETENTION_CATEGORY = 'Retention';

/**
 * The agency's own records-retention schedule, expressed as a floor on call deletion.
 *
 * Deliberately explicit that this is not an auto-purge and not a ceiling: the wording is what a
 * TenantAdmin reads before deciding a number, and "retention" is easily misread as "delete after".
 */
export const RetentionSettings: React.FC = () => {
  const [days, setDays] = useState('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // `loading` starts true and the loader only settles it, so the mount path has no synchronous
  // setState. eslint's react-hooks/set-state-in-effect still flags the call itself — that
  // advisory fires on the same data-loading pattern in all five report components and is
  // unresolved repo-wide (BACKLOG); this file follows the established pattern rather than
  // inventing a different one.
  const loadSettings = useCallback(async () => {
    try {
      const config = await configurationService.getConfiguration(RETENTION_KEY);
      setDays(config.configValue ?? '0');
      setError('');
    } catch (_err) {
      setError('Failed to load retention settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleReset = () => {
    setLoading(true);
    loadSettings();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await configurationService.updateConfiguration(RETENTION_KEY, {
        configValue: days.trim(),
        category: RETENTION_CATEGORY,
      });
      setSuccess('Retention schedule updated.');
      setError('');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      // Surface the server's own message — it names the legal range and why.
      setError((err as ApiError)?.message || 'Failed to update retention settings.');
      setSuccess('');
    } finally {
      setSaving(false);
    }
  };

  const parsed = Number(days.trim());
  const isValid = Number.isInteger(parsed) && parsed >= 0 && parsed <= 36500;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading retention settings" />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Records Retention
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>This is a floor on deletion, not an automatic purge</AlertTitle>
        Nothing is ever deleted because of this setting. It only refuses a delete request that
        arrives before a call has reached the age below. Set it to your agency&apos;s own
        records-retention schedule.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Minimum retention before deletion (days)"
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              inputProps={{ min: 0, max: 36500, 'aria-describedby': 'retention-help' }}
              error={!isValid}
              helperText={
                isValid
                  ? 'Typical values: 31, 90, 180 or 365 days, depending on state law.'
                  : 'Enter a whole number of days between 0 and 36,500.'
              }
              sx={{ maxWidth: 360 }}
            />

            <Typography id="retention-help" variant="body2" color="text.secondary">
              The platform enforces a minimum of its own, and the longer of the two applies — so
              this setting can only make retention stricter, never weaker. Leave it at 0 to defer
              entirely to the platform floor.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !isValid}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outlined" onClick={handleReset} disabled={saving}>
                Reset
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
